import { useState } from 'react';
import { useCustom } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, Pencil } from 'lucide-react';
import {
  RESPONSE_MODES, STATUSES, ROUNDS, statusBadgeClass,
  type Section, type QuestionListRow,
} from './constants';

const PAGE_SIZE = 25;

export function QuestionsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [status, setStatus] = useState('');
  const [round, setRound] = useState('');

  // Reset to page 1 whenever a filter changes.
  const onFilter = (setter: (v: string) => void) => (v: string) => { setter(v); setPage(1); };

  const { data: sectionsData } = useCustom<{ sections: Section[] }>({
    url: '/v1/admin/sections', method: 'get',
  });
  const sections = sectionsData?.data?.sections ?? [];

  const query: Record<string, string | number> = { page, limit: PAGE_SIZE };
  if (q.trim()) query.q = q.trim();
  if (sectionId) query.section_id = sectionId;
  if (status) query.status = status;
  if (round) query.round = round;

  const { data, isLoading, isError, error } = useCustom<{
    questions: QuestionListRow[]; total: number; page: number; limit: number;
  }>({ url: '/v1/admin/questions', method: 'get', config: { query } });

  const rows = data?.data?.questions ?? [];
  const total = data?.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Onboarding Questions</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} question{total === 1 ? '' : 's'} across all sections &amp; rounds.</p>
        </div>
        <button
          onClick={() => navigate('/questions/new')}
          className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" /> New question
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text" value={q} placeholder="Search text / code…"
          onChange={(e) => onFilter(setQ)(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-56"
        />
        <select value={sectionId} onChange={(e) => onFilter(setSectionId)(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
          <option value="">All sections</option>
          {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
        <select value={status} onChange={(e) => onFilter(setStatus)(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={round} onChange={(e) => onFilter(setRound)(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
          <option value="">All rounds</option>
          {ROUNDS.map((r) => <option key={r} value={r}>Round {r}</option>)}
        </select>
      </div>

      {isError && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-sm text-destructive mb-4">
          {error instanceof Error ? error.message : 'Failed to load questions'}
        </div>
      )}

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Question</th>
              <th className="px-3 py-2 font-medium">Section</th>
              <th className="px-3 py-2 font-medium">Round</th>
              <th className="px-3 py-2 font-medium">Mode</th>
              <th className="px-3 py-2 font-medium">Dim.</th>
              <th className="px-3 py-2 font-medium">Wt</th>
              <th className="px-3 py-2 font-medium">Opts</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No questions match these filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/questions/${r.id}/edit`)}>
                <td className="px-3 py-2 max-w-md">
                  <div className="truncate">{r.question_text}</div>
                  <div className="text-xs text-muted-foreground">{r.legacy_q_number}</div>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{r.section_title}</td>
                <td className="px-3 py-2">{r.round}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.response_mode}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.dimension ?? '—'}</td>
                <td className="px-3 py-2">{r.weight}</td>
                <td className="px-3 py-2">{r.option_count || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${statusBadgeClass(r.status)}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm">
        <span className="text-muted-foreground">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 border rounded-md hover:bg-muted/50 disabled:opacity-50">Previous</button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 border rounded-md hover:bg-muted/50 disabled:opacity-50">Next</button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-3">
        Response modes: {RESPONSE_MODES.join(', ')}. Editing writes directly to the live onboarding catalog.
      </p>
    </div>
  );
}
